import { yupResolver } from '@hookform/resolvers/yup';
import { TextField } from '@material-ui/core';
import React, { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ClientCreateForm } from 'types';
import { sanitizeAlphaNumeric, sanitizeAlphaNumericAndSpecial, sanitizeNumber } from 'utils/formatter';
import { clientCreateStep1Schema } from './Step1.yup';
import { useStyles } from './styles';
import { ClientCreateStepProps } from './types';

export const Step1: React.FC<ClientCreateStepProps> = ({ onSubmit, formName, previousStepData }) => {
  const classes = useStyles();
  const { t } = useTranslation('translation', { keyPrefix: 'clients' });
  const schema = useMemo(() => clientCreateStep1Schema(t), [t]);

  const { control, handleSubmit, setValue } = useForm<ClientCreateForm>({
    mode: 'onBlur',
    defaultValues: previousStepData,
    resolver: yupResolver(schema),
  });

  return (
    <form className={classes.form} id={formName} onSubmit={handleSubmit(onSubmit)}>
      <div className={classes.fieldLine}>
        <div className={classes.sharedLine}>
          <Controller
            name="name"
            control={control}
            render={({ field: { ref, onChange, ...inputProps }, fieldState }) => (
              <TextField
                fullWidth
                id="name"
                variant="outlined"
                label={t('create.step1.name')}
                data-qa-id={t('create.step1.name')}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                inputRef={ref}
                InputLabelProps={{ required: true }}
                onChange={e => {
                  setValue('name', sanitizeAlphaNumeric(e.target.value));
                }}
                {...inputProps}
              />
            )}
          />
          <Controller
            name="code"
            control={control}
            render={({ field: { ref, onChange, ...inputProps }, fieldState }) => (
              <TextField
                fullWidth
                id="code"
                variant="outlined"
                label={t('create.step1.code')}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                inputRef={ref}
                inputProps={{ maxLength: 10 }}
                InputLabelProps={{ required: true }}
                onChange={e => {
                  setValue('code', sanitizeAlphaNumericAndSpecial(e.target.value));
                }}
                {...inputProps}
              />
            )}
          />
        </div>
      </div>
      <div className={classes.fieldLine}>
        <Controller
          name="fullName"
          control={control}
          render={({ field: { ref, onChange, ...inputProps }, fieldState }) => (
            <TextField
              fullWidth
              id="fullName"
              variant="outlined"
              label={t('create.step1.fullName')}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              inputRef={ref}
              InputLabelProps={{ required: true }}
              onChange={e => {
                setValue('fullName', sanitizeAlphaNumeric(e.target.value));
              }}
              {...inputProps}
            />
          )}
        />
      </div>
      <div className={classes.fieldLine}>
        <Controller
          name="addressLine"
          control={control}
          render={({ field: { ref, onChange, ...inputProps }, fieldState }) => (
            <TextField
              fullWidth
              id="addressLine"
              variant="outlined"
              label={t('create.step1.address')}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              inputRef={ref}
              onChange={e => {
                setValue('addressLine', sanitizeAlphaNumeric(e.target.value));
              }}
              {...inputProps}
            />
          )}
        />
      </div>
      <div className={classes.fieldLine}>
        <div className={classes.sharedLine}>
          <Controller
            name="postalCode"
            control={control}
            render={({ field: { ref, onChange, ...inputProps }, fieldState }) => (
              <TextField
                fullWidth
                id="postalCode"
                variant="outlined"
                label={t('create.step1.postalCode')}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                inputRef={ref}
                onChange={e => {
                  setValue('postalCode', sanitizeAlphaNumeric(e.target.value));
                }}
                InputLabelProps={{ required: true }}
                {...inputProps}
              />
            )}
          />
          <Controller
            name="city"
            control={control}
            render={({ field: { ref, onChange, ...inputProps }, fieldState }) => (
              <TextField
                fullWidth
                id="city"
                variant="outlined"
                label={t('create.step1.city')}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                inputRef={ref}
                onChange={e => {
                  setValue('city', sanitizeAlphaNumeric(e.target.value));
                }}
                InputLabelProps={{ required: true }}
                {...inputProps}
              />
            )}
          />
        </div>
      </div>
      <div className={classes.fieldLine}>
        <div className={classes.sharedLine}>
          <Controller
            name="phoneNumber"
            control={control}
            render={({ field: { ref, onChange, ...inputProps }, fieldState }) => (
              <TextField
                fullWidth
                id="phoneNumber"
                variant="outlined"
                label={t('create.step1.phone')}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                onChange={e => {
                  setValue('phoneNumber', sanitizeNumber(e.target.value));
                }}
                inputRef={ref}
                inputProps={{ maxLength: 12 }}
                {...inputProps}
              />
            )}
          />
          <Controller
            name="inhabitants"
            control={control}
            render={({ field: { ref, onChange, ...inputProps }, fieldState }) => (
              <TextField
                fullWidth
                id="inhabitants"
                variant="outlined"
                label={t('create.step1.inhabitants')}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                onChange={e => {
                  setValue('inhabitants', sanitizeNumber(e.target.value));
                }}
                inputRef={ref}
                {...inputProps}
              />
            )}
          />
        </div>
      </div>
    </form>
  );
};
